import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { FaGraduationCap, FaBriefcase } from 'react-icons/fa';
import { motion } from 'framer-motion';
import education from '../data/education';
import experience from '../data/experience';

const monthIndex = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
const parseDate = (date) => {
  if (!date) return new Date(0);
  const match = date.match(/^([A-Za-z]+)\s+(\d{4})/);
  return match ? new Date(parseInt(match[2]), monthIndex[match[1]] ?? 0) : new Date(0);
};

function SectionHeading({ icon: Icon, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 2.5, md: 3 } }}>
      <Box sx={{ width: 3, height: 22, backgroundColor: '#C4785D', borderRadius: 2, flexShrink: 0 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Icon size={17} color="#877D72" />
        <Typography variant="h5" component="h2" sx={{ fontSize: { xs: '1.3rem', md: '1.4rem' }, color: '#EAE3D6', fontWeight: 600, letterSpacing: '-0.015em' }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function TimelineEntry({ children, index, isLast }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
    >
      <Box sx={{ display: 'flex', gap: 2, position: 'relative' }}>
        {/* Timeline spine */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, pt: 0.4 }}>
          <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#C4785D',
            flexShrink: 0,
            mt: 0.2,
            boxShadow: '0 0 0 3px rgba(196,120,93,0.12)',
          }} />
          {!isLast && (
            <Box sx={{
              width: 1,
              flex: 1,
              backgroundColor: 'rgba(196,120,93,0.15)',
              mt: 0.75,
              mb: 0,
              minHeight: 24,
            }} />
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, pb: isLast ? 0 : 3 }}>
          {children}
        </Box>
      </Box>
    </motion.div>
  );
}

function ExperienceEntry({ exp, index, isLast }) {
  const dateLabel = `${exp.startDate} – ${exp.current ? 'Present' : exp.endDate}`;

  return (
    <TimelineEntry index={index} isLast={isLast}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.02rem' }, color: '#EAE3D6', lineHeight: 1.3, mb: 0.25 }}>
            {exp.role}
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#C4785D', fontWeight: 500 }}>
            {exp.company}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#524A42', whiteSpace: 'nowrap', fontWeight: 500 }}>
            {dateLabel}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#3D3830', mt: 0.25 }}>
            {exp.location}
          </Typography>
        </Box>
      </Box>

      {exp.highlights && exp.highlights.length > 0 && (
        <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.9 }}>
          {exp.highlights.map((h, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: 'rgba(196,120,93,0.45)', mt: '8px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.7, color: '#877D72' }}>{h}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </TimelineEntry>
  );
}

function EducationEntry({ edu, index, isLast }) {
  const dateLabel = `${edu.startDate} – ${edu.current ? 'Present' : edu.endDate}`;

  return (
    <TimelineEntry index={index} isLast={isLast}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.02rem' }, color: '#EAE3D6', lineHeight: 1.3, mb: 0.25 }}>
            {edu.degree}
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#C4785D', fontWeight: 500 }}>
            {edu.institution}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#524A42', whiteSpace: 'nowrap', fontWeight: 500 }}>
            {dateLabel}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#3D3830', mt: 0.25 }}>
            {edu.location}
          </Typography>
        </Box>
      </Box>
    </TimelineEntry>
  );
}

function BackgroundSection() {
  const hasEducation = education && education.length > 0;
  const hasExperience = experience && experience.length > 0;

  if (!hasEducation && !hasExperience) return null;

  const sortedEducation = [...education].sort((a, b) => parseDate(b.startDate) - parseDate(a.startDate));
  const sortedExperience = [...experience].sort((a, b) => parseDate(b.startDate) - parseDate(a.startDate));

  return (
    <Box id="background" component="section" sx={{ py: { xs: 4, sm: 5.5, md: 7 }, background: 'transparent' }}>
      <Container maxWidth="lg">
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 5, md: 6 },
          alignItems: 'start',
        }}>

          {hasExperience && (
            <Box id="experience">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <SectionHeading icon={FaBriefcase} label="Experience" />
              </motion.div>
              <Box>
                {sortedExperience.map((exp, i) => (
                  <ExperienceEntry key={exp.id} exp={exp} index={i} isLast={i === sortedExperience.length - 1} />
                ))}
              </Box>
            </Box>
          )}

          {hasEducation && (
            <Box id="education">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <SectionHeading icon={FaGraduationCap} label="Education" />
              </motion.div>
              <Box>
                {sortedEducation.map((edu, i) => (
                  <EducationEntry key={edu.id} edu={edu} index={i} isLast={i === sortedEducation.length - 1} />
                ))}
              </Box>
            </Box>
          )}

        </Box>
      </Container>
    </Box>
  );
}

export default BackgroundSection;
